// components/models/Kling25TurboControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import { Kling25TurboParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface Kling25TurboControlPanelProps {
  params: Kling25TurboParams;
  setModelParams: (params: (prevParams: { [model: string]: Kling25TurboParams }) => { [model: string]: Kling25TurboParams }) => void;
  diffusionModel: string;
}

const defaultKling25TurboParams: Kling25TurboParams = {
  prompt: '',
  negative_prompt: '',
  duration: 5,
  aspect_ratio: '16:9',
  cfg_scale: 0.5,
  seed: -1,
};

export const getDefaultKling25TurboParams = (): Kling25TurboParams => ({ ...defaultKling25TurboParams });

// The endpoint takes a "5" | "10" enum, converted from this number in app/api/film/route.ts.
const DURATION_OPTIONS = [
  { value: '5', label: '5s' },
  { value: '10', label: '10s' },
];
const ASPECT_RATIO_OPTIONS: NonNullable<Kling25TurboParams['aspect_ratio']>[] = ['16:9', '9:16', '1:1'];

const Kling25TurboControlPanel: React.FC<Kling25TurboControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling25TurboControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' || name === 'negative_prompt'
        ? { value_length: (newValue as string).length }
        : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'Kling25TurboControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling25TurboControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: name === 'duration' ? Number(value) : value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Kling25TurboControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt ?? ''}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="kling-2-5-turbo-duration"
          label="Duration"
          value={String(params.duration)}
          options={DURATION_OPTIONS}
          onChange={(value) => handleSelectChange('duration', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="kling-2-5-turbo-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio ?? '16:9'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="CFG Scale"
          name="cfg_scale"
          min={0}
          max={1}
          step={0.05}
          value={params.cfg_scale ?? 0.5}
          onChange={handleParamChange}
          description="How strictly the model follows the prompt."
        />
      </div>
    </>
  );
});

Kling25TurboControlPanel.displayName = 'Kling25TurboControlPanel';

export default Kling25TurboControlPanel;
